import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { tracks } from '../data/tracks'
import { useSoundCloudPlayer } from '../hooks/useSoundCloudPlayer'

interface MusicContextType {
  isPlaying: boolean
  currentTime: number
  currentTrackIndex: number
  track: typeof tracks[0]
  duration: number
  togglePlayPause: () => void
  nextTrack: () => void
  prevTrack: () => void
  systemVolume: number
  isReady: boolean
  isSystemReady: boolean
  iframeRef: React.RefObject<HTMLIFrameElement | null>
  musicVolume: number
  setMusicVolume: (volume: number) => void
}

const MusicContext = createContext<MusicContextType | undefined>(undefined)

interface MusicProviderProps {
  children: ReactNode
  systemVolume: number
}

export function MusicProvider({ children, systemVolume }: MusicProviderProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0)
  const [musicVolume, setMusicVolume] = useState(() => {
    const saved = localStorage.getItem('music_volume')
    return saved ? parseInt(saved, 10) : 40
  })
  const [isSystemReady, setIsSystemReady] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hasLoadedInitialTrack = useRef(false)
  const hasInitialized = useRef(false)
  const loadTrackRef = useRef<((url: string) => void) | null>(null)

  const track = tracks[currentTrackIndex]

  useEffect(() => {
    localStorage.setItem('music_volume', musicVolume.toString())
  }, [musicVolume])

  const finalVolume = Math.round((systemVolume / 100) * (musicVolume / 100) * 100)

  const handleReady = () => {
    if (!hasLoadedInitialTrack.current && loadTrackRef.current) {
      loadTrackRef.current(tracks[0].soundcloudUrl)
      hasLoadedInitialTrack.current = true
      setTimeout(() => setIsSystemReady(true), 300)
    }
  }

  const {
    isReady,
    currentPosition,
    duration,
    initializeWidget,
    loadTrack,
    play,
    pause,
    toggle
  } = useSoundCloudPlayer({
    onReady: handleReady,
    onPlay: () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onFinish: () => {
      setIsPlaying(false)
      setCurrentTrackIndex((prev) => (prev + 1) % tracks.length)
    },
    onError: () => setIsPlaying(false),
    finalVolume
  })

  loadTrackRef.current = loadTrack

  useEffect(() => {
    if (!isReady || !hasLoadedInitialTrack.current) return

    const wasPlaying = isPlaying
    if (wasPlaying) pause()
    
    setIsPlaying(false)
    loadTrack(track.soundcloudUrl)
    
    if (wasPlaying) {
      setTimeout(play, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex, isReady])

  const togglePlayPause = () => {
    if (isReady) toggle()
  }

  const nextTrack = () => {
    if (isReady) {
      setCurrentTrackIndex((prev) => (prev + 1) % tracks.length)
    }
  }

  const prevTrack = () => {
    if (isReady) {
      setCurrentTrackIndex((prev) => (prev - 1 + tracks.length) % tracks.length)
    }
  }

  const handleSetMusicVolume = (volume: number) => {
    setMusicVolume(Math.max(0, Math.min(100, volume)))
  }

  const handleIframeMount = (iframe: HTMLIFrameElement | null) => {
    if (iframe && !hasInitialized.current) {
      iframeRef.current = iframe
      hasInitialized.current = true
      initializeWidget(iframe)
    }
  }

  return (
    <MusicContext.Provider
      value={{
        isPlaying,
        currentTime: currentPosition,
        currentTrackIndex,
        track,
        duration,
        togglePlayPause,
        nextTrack,
        prevTrack,
        systemVolume,
        isReady,
        isSystemReady,
        iframeRef,
        musicVolume,
        setMusicVolume: handleSetMusicVolume
      }}
    >
      <iframe
        ref={handleIframeMount}
        width="0"
        height="0"
        scrolling="no"
        frameBorder="no"
        allow="autoplay"
        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(track.soundcloudUrl)}&auto_play=false&buying=false&sharing=false&download=false&show_artwork=false&show_comments=false&show_playcount=false&show_user=false&visual=false`}
        style={{ display: 'none' }}
        title="SoundCloud Player"
      />
      {children}
    </MusicContext.Provider>
  )
}

export function useMusic() {
  const context = useContext(MusicContext)
  if (!context) {
    throw new Error('useMusic must be used within MusicProvider')
  }
  return context
}